import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLogout, useMenu } from '@refinedev/core';
import { Layout, Menu, Button, Tooltip, Segmented } from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useService, services, ServiceType, isLockedMode, isTaxiOnlyMode, isBusCompanyUser } from '../../contexts/ServiceContext';
import { hasPermission } from '../../hooks/usePermission';
import { tokenManager } from '../../lib/api-client';
import { t } from '../../i18n';

const { Sider } = Layout;

interface CustomSiderProps {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
}


/**
 * Given the current pathname, compute which menu key should be selected
 * and which submenu keys should be open.
 *
 * Refine's useMenu returns menuItems where each item.key is the resource
 * name (e.g. "admin/users"), while the browser location is a path like
 * "/users/123". We build a mapping from every known list-route to its
 * resource key, then match the current pathname against those routes using
 * startsWith so nested routes (show / edit / create) highlight the parent.
 */
function computeMenuSelection(
  menuItems: ReturnType<typeof useMenu>['menuItems'],
  pathname: string,
): { selectedKeys: string[]; openKeys: string[] } {
  // Build route -> key mapping from flat + nested menu items
  const routeToKey: { route: string; key: string; parentKey?: string }[] = [];

  const walk = (items: typeof menuItems, parentKey?: string) => {
    for (const item of items) {
      if (item.route) {
        routeToKey.push({
          route: item.route as string,
          key: item.key ?? (item.name as string),
          parentKey,
        });
      }
      if (item.children?.length) {
        walk(item.children, item.key ?? (item.name as string));
      }
    }
  };
  walk(menuItems);

  // Sort by route length descending so longer (more specific) routes match first
  routeToKey.sort((a, b) => b.route.length - a.route.length);

  // Exact match first, then startsWith
  let match = routeToKey.find((r) => r.route === pathname);
  if (!match) {
    match = routeToKey.find((r) => r.route !== '/' && pathname.startsWith(r.route));
  }
  // Special case: dashboard at "/"
  if (!match && pathname === '/') {
    match = routeToKey.find((r) => r.route === '/');
  }

  const selectedKeys = match ? [match.key] : [];
  const openKeys: string[] = [];
  if (match?.parentKey) {
    openKeys.push(match.parentKey);
    // Also open the grandparent if there is one (group -> submenu -> item)
    const parent = routeToKey.find((r) => r.key === match!.parentKey);
    if (parent?.parentKey) {
      openKeys.push(parent.parentKey);
    }
    // For submenu parents that don't have routes, look them up in the items tree
    const findParentKey = (items: typeof menuItems, targetKey: string): string | undefined => {
      for (const item of items) {
        if (item.children?.length) {
          for (const child of item.children) {
            if ((child.key ?? child.name) === targetKey) {
              return item.key ?? (item.name as string);
            }
            if (child.children?.length) {
              const found = findParentKey([child], targetKey);
              if (found) return item.key ?? (item.name as string);
            }
          }
        }
      }
      return undefined;
    };
    const grandParent = findParentKey(menuItems, match.parentKey);
    if (grandParent && !openKeys.includes(grandParent)) {
      openKeys.push(grandParent);
    }
  }

  return { selectedKeys, openKeys };
}

export const CustomSider = ({ collapsed, setCollapsed }: CustomSiderProps) => {
  const { mutate: logout } = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const { menuItems } = useMenu();
  const { selectedService, setSelectedService } = useService();
  const taxiOnlyMode = isTaxiOnlyMode();
  const lockedMode = isLockedMode();
  const isBusCompany = isBusCompanyUser();

  const user = tokenManager.getUser();
  const isSuperAdmin = user?.isSuperAdmin === true;

  // Check if a menu item passes permission filters
  const passesFilters = (item: { meta?: Record<string, unknown>; route?: string }) => {
    const hideForBusCompany = item.meta?.hideForBusCompany as boolean | undefined;
    const busCompanyOnly = item.meta?.busCompanyOnly as boolean | undefined;
    const forceVisibleForUsernames = item.meta?.forceVisibleForUsernames as string[] | undefined;
    if (isBusCompany && hideForBusCompany) return false;
    if (!isBusCompany && busCompanyOnly) return false;
    // Per-username allowlist: force the item visible regardless of permissions
    // (e.g. read-only viewers who otherwise lack the required permission).
    if (forceVisibleForUsernames && user?.username && forceVisibleForUsernames.includes(user.username)) {
      return true;
    }
    if (!isSuperAdmin && !isBusCompany) {
      const permission = item.meta?.permission as { group: string; action: string } | undefined;
      if (permission && !hasPermission(permission.group as any, permission.action as any)) {
        return false;
      }
    }
    return true;
  };

  // Filter top-level items by service + permissions
  const filteredMenuItems = menuItems.filter((item) => {
    const service = item.meta?.service as string | undefined;
    const serviceMatch = !service || service === 'all' || service === selectedService;
    if (!serviceMatch) return false;
    return passesFilters(item);
  });

  // Build Ant Design menu items with group headers
  const menuItemsForAntd: MenuProps['items'] = useMemo(() => {
    const result: NonNullable<MenuProps['items']> = [];

    for (const item of filteredMenuItems) {
      const isGroup = item.meta?.isGroup === true;

      if (isGroup) {
        // This is a group header. Its children are the actual menu items in this group.
        const groupChildren = (item.children ?? []).filter((child) => {
          const svc = child.meta?.service as string | undefined;
          if (svc && svc !== 'all' && svc !== selectedService) return false;
          return passesFilters(child);
        });
        if (groupChildren.length === 0) continue;

        // Build items for this group
        const groupItems: NonNullable<MenuProps['items']> = [];

        for (const child of groupChildren) {
          if (child.children && child.children.length > 0) {
            // Submenu (e.g. Foydalanuvchilar, Sayohatlar)
            const filteredSubs = child.children.filter((sub) => {
              const svc = sub.meta?.service as string | undefined;
              if (svc && svc !== 'all' && svc !== selectedService) return false;
              return passesFilters(sub);
            });
            if (filteredSubs.length === 0) continue;
            groupItems.push({
              key: child.key ?? child.name,
              icon: child.icon,
              label: child.label,
              children: filteredSubs.map((sub) => ({
                key: sub.key ?? sub.name,
                icon: sub.icon,
                label: sub.label,
                onClick: () => navigate(sub.route || '/'),
              })),
            });
          } else {
            // Simple item
            groupItems.push({
              key: child.key ?? child.name,
              icon: child.icon,
              label: child.label,
              onClick: () => navigate(child.route || '/'),
            });
          }
        }

        if (groupItems.length > 0) {
          // Insert the group divider/label then items
          result.push({
            type: 'group' as const,
            key: `grp-${item.key ?? item.name}`,
            label: collapsed ? null : (item.label as string),
            children: groupItems,
          });
        }
      } else if (item.children && item.children.length > 0) {
        // Top-level submenu outside any group (shouldn't happen with new structure, but keep for safety)
        const filteredChildren = item.children.filter((child) => {
          const svc = child.meta?.service as string | undefined;
          if (svc && svc !== 'all' && svc !== selectedService) return false;
          return passesFilters(child);
        });
        if (filteredChildren.length === 0) continue;
        result.push({
          key: item.key ?? item.name,
          icon: item.icon,
          label: item.label,
          children: filteredChildren.map((child) => ({
            key: child.key ?? child.name,
            icon: child.icon,
            label: child.label,
            onClick: () => navigate(child.route || '/'),
          })),
        });
      } else {
        // Top-level item outside any group (Dashboard, Live Map)
        result.push({
          key: item.key ?? item.name,
          icon: item.icon,
          label: item.label,
          onClick: () => navigate(item.route || '/'),
        });
      }
    }

    return result;
  }, [filteredMenuItems, collapsed, selectedService, navigate]);

  // Compute selected + open keys from current pathname
  const { selectedKeys, openKeys: computedOpenKeys } = useMemo(
    () => computeMenuSelection(filteredMenuItems, location.pathname),
    [filteredMenuItems, location.pathname],
  );

  // Manage open submenu keys with state so users can manually open/close
  const [managedOpenKeys, setManagedOpenKeys] = useState<string[]>(computedOpenKeys);

  // When pathname changes, ensure the relevant submenus are open
  useEffect(() => {
    setManagedOpenKeys((prev) => {
      const merged = new Set([...prev, ...computedOpenKeys]);
      return Array.from(merged);
    });
  }, [computedOpenKeys.join(',')]);

  const handleServiceChange = (value: string | number) => {
    const key = value as ServiceType;
    const service = services.find((s) => s.key === key);
    if (service?.isAvailable) {
      setSelectedService(key);
      navigate('/');
    }
  };

  // Available services for the segmented control
  const availableServices = services.filter((s) => s.isAvailable);

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      width={260}
      collapsedWidth={80}
      style={{
        overflow: 'hidden',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 100,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        {/* Logo */}
        <div
          style={{
            padding: collapsed ? '20px 16px' : '24px 20px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            minHeight: 80,
          }}
        >
          <img
            src="/safargo.png"
            alt="Safaro"
            style={{
              width: collapsed ? 42 : 46,
              height: collapsed ? 42 : 46,
              borderRadius: 12,
              flexShrink: 0,
              objectFit: 'cover',
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          {!collapsed && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span
                style={{
                  color: '#FFFFFF',
                  fontSize: 24,
                  fontWeight: 700,
                  letterSpacing: '-0.5px',
                }}
              >
                Safaro Admin
              </span>
              {lockedMode && (
                <span
                  style={{
                    color: taxiOnlyMode ? '#3498DB' : '#9B59B6',
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                    marginTop: 2,
                  }}
                >
                  {taxiOnlyMode ? 'TAXI & POCHTA' : 'AVTOBUSLAR'}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Service Selector - minimal segmented control */}
        {!lockedMode && !isBusCompany && (
          <div
            style={{
              padding: collapsed ? '10px 8px' : '10px 16px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            }}
          >
            {collapsed ? (
              <Tooltip title={selectedService === 'taxi' ? 'Taxi' : 'Bus'} placement="right">
                <div
                  onClick={() => {
                    const next = selectedService === 'taxi' ? 'bus' : 'taxi';
                    const svc = services.find((s) => s.key === next);
                    if (svc?.isAvailable) handleServiceChange(next);
                  }}
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    background:
                      selectedService === 'taxi'
                        ? 'rgba(52, 152, 219, 0.2)'
                        : 'rgba(155, 89, 182, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    cursor: 'pointer',
                    border:
                      selectedService === 'taxi'
                        ? '1px solid rgba(52, 152, 219, 0.35)'
                        : '1px solid rgba(155, 89, 182, 0.35)',
                    transition: 'all 0.2s ease',
                    fontSize: 11,
                    fontWeight: 700,
                    color:
                      selectedService === 'taxi'
                        ? '#3498DB'
                        : '#9B59B6',
                  }}
                >
                  {selectedService === 'taxi' ? 'T' : 'B'}
                </div>
              </Tooltip>
            ) : (
              <Segmented
                value={selectedService}
                onChange={handleServiceChange}
                block
                options={availableServices.map((s) => ({
                  value: s.key,
                  label: s.key === 'taxi' ? t('service.taxi') : s.key === 'bus' ? t('service.bus') : s.label,
                  disabled: !s.isAvailable,
                }))}
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  borderRadius: 8,
                  padding: 2,
                }}
              />
            )}
          </div>
        )}

        {/* Main Menu */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '8px 0',
          }}
        >
          <style>{`
            /* Group label styling */
            .safaro-sidebar .ant-menu-item-group-title {
              color: rgba(255, 255, 255, 0.35) !important;
              font-size: 10px !important;
              font-weight: 700 !important;
              letter-spacing: 1.2px !important;
              text-transform: uppercase !important;
              padding: 16px 24px 4px !important;
              line-height: 16px !important;
            }
            /* Reduce menu item height */
            .safaro-sidebar .ant-menu-item {
              height: 40px !important;
              line-height: 40px !important;
              margin-block: 2px !important;
            }
            .safaro-sidebar .ant-menu-submenu-title {
              height: 40px !important;
              line-height: 40px !important;
              margin-block: 2px !important;
            }
            /* Selected item accent */
            .safaro-sidebar .ant-menu-item-selected {
              background: rgba(52, 152, 219, 0.15) !important;
              border-right: none !important;
              position: relative;
            }
            .safaro-sidebar .ant-menu-item-selected::before {
              content: '';
              position: absolute;
              left: 0;
              top: 6px;
              bottom: 6px;
              width: 3px;
              border-radius: 0 3px 3px 0;
              background: linear-gradient(180deg, #3498DB 0%, #2980B9 100%);
            }
            /* Submenu children items */
            .safaro-sidebar .ant-menu-sub .ant-menu-item {
              height: 36px !important;
              line-height: 36px !important;
            }
            /* Hide first group title top padding */
            .safaro-sidebar .ant-menu-item-group:first-child .ant-menu-item-group-title {
              padding-top: 8px !important;
            }
            /* Add gap between groups */
            .safaro-sidebar .ant-menu-item-group + .ant-menu-item-group {
              margin-top: 6px;
            }
            /* Segmented control dark theme overrides */
            .safaro-sidebar .ant-segmented {
              background: rgba(255, 255, 255, 0.06) !important;
              height: 34px;
            }
            .safaro-sidebar .ant-segmented .ant-segmented-item {
              color: rgba(255, 255, 255, 0.55) !important;
              font-weight: 600;
              font-size: 13px;
            }
            .safaro-sidebar .ant-segmented .ant-segmented-item-selected {
              background: rgba(52, 152, 219, 0.25) !important;
              color: #fff !important;
              box-shadow: none !important;
            }
            .safaro-sidebar .ant-segmented .ant-segmented-thumb {
              background: rgba(52, 152, 219, 0.25) !important;
              box-shadow: none !important;
            }
            /* Scrollbar styling */
            .safaro-sidebar ::-webkit-scrollbar {
              width: 4px;
            }
            .safaro-sidebar ::-webkit-scrollbar-thumb {
              background: rgba(255, 255, 255, 0.1);
              border-radius: 4px;
            }
            .safaro-sidebar ::-webkit-scrollbar-track {
              background: transparent;
            }
          `}</style>
          <div className="safaro-sidebar" style={{ height: '100%', overflow: 'auto' }}>
            <Menu
              theme="dark"
              mode="inline"
              selectedKeys={selectedKeys}
              openKeys={collapsed ? [] : managedOpenKeys}
              onOpenChange={(keys) => {
                if (!collapsed) setManagedOpenKeys(keys as string[]);
              }}
              items={menuItemsForAntd}
              style={{
                background: 'transparent',
                border: 'none',
              }}
            />
          </div>
        </div>

        {/* Bottom Section */}
        <div
          style={{
            padding: collapsed ? '12px 12px' : '12px 16px',
            borderTop: '1px solid rgba(255, 255, 255, 0.06)',
          }}
        >
          {/* Logout Button */}
          <Tooltip title={collapsed ? t('common.logout') : ''} placement="right">
            <Button
              type="text"
              icon={<LogoutOutlined style={{ fontSize: 16 }} />}
              onClick={() => logout()}
              style={{
                width: '100%',
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'flex-start',
                gap: 12,
                color: '#ff6b6b',
                marginBottom: 10,
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 500,
                paddingLeft: collapsed ? 0 : 12,
              }}
            >
              {!collapsed && t('common.logout')}
            </Button>
          </Tooltip>

          {/* Collapse Toggle */}
          <Button
            type="default"
            onClick={() => setCollapsed(!collapsed)}
            style={{
              width: '100%',
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              color: 'rgba(255, 255, 255, 0.85)',
              borderRadius: 10,
              background: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            {collapsed ? (
              <RightOutlined style={{ fontSize: 14 }} />
            ) : (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <LeftOutlined style={{ fontSize: 12 }} />
                <span>{t('common.collapseMenu')}</span>
              </span>
            )}
          </Button>
        </div>
      </div>
    </Sider>
  );
};
